import { useEffect, useMemo, useState, useRef } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import { RadioGroup, ServiceCard } from "@aqar-app/aqar-design-system";
import {
  dehydrateApolloClient,
  getMaintainedParam,
  initApolloClient,
} from "../../lib/apolloClient";
import {
  GET_OWNER_LISTINGS,
  GET_USER_WITH_REVIEWS,
} from "../../graphql/queries/user";
import { useAuthState } from "../../contexts/auth/auth.provider";
import config from "../../common/config";
import UserFees from "../../containers/UserFees/UserFees";
import {
  buildFileSrc,
  ConditionalWrapper,
  convertToArabicNumerals,
  financialFormat,
  hasHinduNumerals,
} from "../../lib/helpers";
import RichOfficeMeta from "../../components/richResults/office.rich.meta";
import style from "./user.module.scss";
import UserListings from "../../containers/UserListings/UserListings";
import { FIND_LISTING } from "../../graphql/queries/find";
import { InitialResolver } from "../../lib/use.middlewares";
import { UserCampaigns } from "@/src/pages/user/campaigns";
import { usePath } from "@/contexts/path/path";
import { trackGenericWalletFromAccountClick } from "@/lib/tracking/trackWalletEvents";
import AuthProfileCard from "@/components/UserScreen/AuthProfileCard";
import ProfileCard from "@/components/UserScreen/ProfileCard/ProfileCard";
import {
  trackBrokerPaymentRequestsClick,
  trackClientPaymentRequestsClick,
} from "@/lib/tracking/trackExclusiveUnitBookingEvents";
import { trackEstablishmentPhotoClickEvent } from "@/lib/tracking/trackEstablishmentPhotoUpload";

const UserServices = [
  {
    name: "خدمات الترويج",
    icon: "promotion",
    path: `/user/promotion`,
    key: "promotion",
    filter: () => true,
  },
  {
    name: "طلبات الدفع",
    icon: "promotion",
    path: `/user/payment-requests`,
    key: "payment-requests",
    filter: (user) =>
      user?.permissions?.includes(
        "ACCESS_TO_INIT_REAL_ESTATE_PROJECT_PAYMENT_REQUEST",
      ),
  },
  {
    name: "طلبات حجز الوحدات",
    icon: "promotion",
    path: `/user/my-payment-requests`,
    key: "my-payment-requests",
    filter: () => true,
  },
  {
    name: "توثيق النفاذ الوطني",
    icon: "iam-verified",
    path: `/user-actions/verify-iam`,
    key: "iam_verified",
    filter: (user) => !user?.iam_verified,
  },

  {
    name: "طلبات الترخيص",
    icon: "userRequests",
    path: `/mlr`,
    badge: true,
    filter: () => true,
  },
  {
    name: "توثيق المنشأة",
    icon: "office-verify-colored",
    path: `/user/verify-office`,
    filter: (user) => user?.type !== 1,
  },
  {
    name: "",
    key: "establishment-account",
    icon: "office-verify-colored",
    path: `/user/establishment-account`,
    filter: (user) => user?.type === 1,
  },
  {
    name: "حملات التمييز",
    icon: "star-rounded-brand",
    path: `/user/campaigns`,
    filter: (user) => user?.can_sov,
  },
  {
    name: "تمييز الإعلانات",
    icon: "WebFeatured-colored",
    path: `/user/campaigns`,
    filter: () => false,
  },
  // {
  //   name: "حجوزاتي",
  //   icon: "dayReservedCalendar",
  //   path: `/user/bookings`,
  //   filter: () => true,
  // },
  // {
  //   name: "سجل الحجوزات",
  //   icon: "reservedCalendar",
  //   path: `/user/reservations`,
  //   filter: (user) => user?.has_daily_rentable,
  // },
  {
    name: "الحجوزات",
    icon: "reservedCalendar",
    path: `/user/bookings`,
    filter: () => true,
  },
  {
    name: "لوحة التحكم",
    icon: "Packages-colored",
    path: `${config.AQAR_WEB}/offices-management`,
    key: "paid",
    filter: (user) => user?.paid,
    disableClientSideNav: true,
  },
  {
    name: "عقار بلس",
    icon: "Aqar_plus",
    path: `/aqar-plus`,
    filter: () => true,
  },

  {
    name: "إضافة رخصة",
    icon: "REGA-colored",
    path: `/user-actions/add-rega-license`,
    key: "rega_approved",
    filter: (user) => !user?.rega_approved && user?.iam_verified,
  },
];
const USER_LISTINGS_TYPES = [
  { label: "كل الإعلانات", value: "listings" },
  // { label: "إعلانات مسودة", value: "drafts" },
  // { label: "إعلانات منتهية", value: "expired_listings" },
  { label: "المدفوعات", value: "fees" },
  { label: "الحملات", value: "campaigns", filter: (user) => user?.can_sov },
];
const ComponentsMap = {
  fees: UserFees,
  listings: UserListings,
  // drafts: UserListings,
  // expired_listings: UserListings,
  campaigns: UserCampaigns,
};
function UserPage({ userListings, userListingsTotal }) {
  const { token, user: loggedInUser } = useAuthState();
  const router = useRouter();
  const { previousPath } = usePath();
  const { userId } = router.query;
  const { asPath } = router;
  const isOwner =
    loggedInUser?.user_id && `${loggedInUser?.user_id}` === userId;
  const [currentListings, setCurrentListings] = useState(
    router.asPath.split("#")[1] || "listings",
  );

  const scrollRef = useRef(null);

  const Component = ComponentsMap[currentListings];
  useEffect(() => {
    setCurrentListings(asPath.split("#")[1] || "listings");
  }, [asPath]);

  const handleSwitcherChange = (listingsType) => {
    router.replace({
      hash: listingsType,
    });
  };

  const {
    data: userData,
    loading,
    error,
  } = useQuery(GET_USER_WITH_REVIEWS, {
    skip: !userId,
    variables: { user_id: userId },
  });

  const user = useMemo(() => {
    const u = {
      ...(userData?.User?.get || {}),
      reviewsCount: userData?.User?.reviews?.total,
    };
    return { ...u, user_id: u.user_id + "", id: u.user_id };
  }, [userData]);

  const ownerServices = useMemo(() => {
    if (!isOwner) return null;

    return (
      <div style={{ marginBottom: "12px"}}>
        {/*<div ref={scrollRef} className={style.servicesList}>*/}
        {/*  <div className={style.item}>*/}
        {/*    <Link*/}
        {/*      onClick={() => {*/}
        {/*        trackGenericWalletFromAccountClick({*/}
        {/*          source: previousPath || decodeURI(document.referrer),*/}
        {/*          target: "wallet",*/}
        {/*        });*/}
        {/*      }}*/}
        {/*      href="/user/wallet"*/}
        {/*    >*/}
        {/*      <ServiceCard*/}
        {/*        label={`المحفظة (${*/}
        {/*          loggedInUser?.points_balance*/}
        {/*            ? financialFormat(loggedInUser?.points_balance * 0.01) +*/}
        {/*              ") ريال"*/}
        {/*            : "لا يوجد رصيد)"*/}
        {/*        }`}*/}
        {/*        iconProps={{ type: "wallet" }}*/}
        {/*        iconClassName={style.serviceIcon}*/}
        {/*      />*/}
        {/*    </Link>*/}
        {/*  </div>*/}

        {/*  {UserServices.filter((service) => {*/}
        {/*    return !service.filter || service.filter(loggedInUser);*/}
        {/*  }).map((service) =>*/}
        {/*    service.key?.match(/payment-requests/) ? (*/}
        {/*      <div key={service.name} className={style.item}>*/}
        {/*        <Link*/}
        {/*          href={service.path}*/}
        {/*          passHref*/}
        {/*          onClick={() => {*/}
        {/*            if (service.key === "payment-requests") {*/}
        {/*              trackBrokerPaymentRequestsClick({*/}
        {/*                target: "payment_requests",*/}
        {/*              });*/}
        {/*            }*/}
        {/*            if (service.key === "my-payment-requests") {*/}
        {/*              trackClientPaymentRequestsClick({*/}
        {/*                target: "unit_reservation_requests",*/}
        {/*              });*/}
        {/*            }*/}
        {/*          }}*/}
        {/*        >*/}
        {/*          <div*/}
        {/*            className={style.serviceCard}*/}
        {/*            style={{ minWidth: "120px" }}*/}
        {/*          >*/}
        {/*            <span className={style.newTag}>جديد</span>*/}
        {/*            <svg*/}
        {/*              width="30"*/}
        {/*              height="28"*/}
        {/*              viewBox="0 0 30 28"*/}
        {/*              fill="none"*/}
        {/*              xmlns="http://www.w3.org/2000/svg"*/}
        {/*            >*/}
        {/*              <path*/}
        {/*                fill-rule="evenodd"*/}
        {/*                clip-rule="evenodd"*/}
        {/*                d="M17 0C20.866 0 24 3.13402 24 7.00003C24 10.866 20.866 14 17 14C13.134 14 10 10.866 10 7.00003C10 3.13402 13.134 0 17 0Z"*/}
        {/*                fill="#666666"*/}
        {/*              />*/}
        {/*              <path*/}
        {/*                fill-rule="evenodd"*/}
        {/*                clip-rule="evenodd"*/}
        {/*                d="M3.17059 17.7839L7.54982 25.7716C7.77732 26.1866 7.63455 26.7183 7.23253 26.9531L5.63203 27.8882C5.23002 28.1231 4.71496 27.9757 4.48746 27.5607L0.108295 19.573C-0.119199 19.158 0.0235711 18.6263 0.425583 18.3915L2.02609 17.4564C2.42804 17.2215 2.9431 17.369 3.17059 17.7839ZM4.99295 17.9204L8.31443 23.9789L8.62107 23.7961L16.1296 25.873C17.2097 26.1718 18.2515 26.0307 19.2035 25.4256L29.2911 19.2257C30.7654 18.2887 30.1141 14.1875 25.68 16.8534L20.24 19.9028C19.4433 20.3817 18.6074 20.5103 17.6971 20.3601C16.9383 20.2347 15.1232 19.7107 13.3092 19.2089C12.98 19.1179 12.7847 18.7686 12.8729 18.4288C12.9611 18.089 13.2994 17.8873 13.6285 17.9784L18.753 19.3958C18.8036 19.2898 18.8451 19.175 18.8771 19.0519C19.1066 18.1679 18.5808 17.296 17.7373 17.0137C15.465 16.5906 13.2367 15.6097 11.8722 15.1529C10.6483 14.743 9.51609 15.2153 8.40258 15.8812L4.99295 17.9204Z"*/}
        {/*                fill="#666666"*/}
        {/*              />*/}
        {/*              <path*/}
        {/*                d="M17.898 10.087C17.7863 10.3702 17.7124 10.6776 17.6841 10.9999L19.7861 10.4842C19.8978 10.201 19.9716 9.89361 20 9.57129L17.898 10.087Z"*/}
        {/*                fill="white"*/}
        {/*              />*/}
        {/*              <path*/}
        {/*                d="M19.786 8.77006C19.8978 8.4869 19.9717 8.17948 20 7.85716L18.4208 8.2389V7.37802L19.786 7.05577C19.8978 6.77261 19.9717 6.46519 20 6.14287L18.4207 6.51707V3.42108C18.1384 3.60224 17.8877 3.84337 17.6839 4.12781V6.69613L16.9471 6.87512V3C16.6647 3.18109 16.414 3.42229 16.2102 3.70673V7.05404L14.5616 7.45443C14.4498 7.73759 14.3759 8.04501 14.3475 8.36733L16.2102 7.91492V8.99905L14.2139 9.48387C14.1022 9.76703 14.0283 10.0745 14 10.3968L16.0895 9.88928C16.2596 9.84885 16.4058 9.73392 16.5009 9.57575L16.8841 8.92646C16.9239 8.85928 16.9471 8.77816 16.9471 8.69093V7.73593L17.6839 7.55694V9.27871L19.786 8.77006Z"*/}
        {/*                fill="white"*/}
        {/*              />*/}
        {/*            </svg>*/}
        {/*            <div className={style.label}>{service.name}</div>*/}
        {/*          </div>*/}
        {/*        </Link>*/}
        {/*      </div>*/}
        {/*    ) : service.key === "establishment-account" ? (*/}
        {/*      <div key={service.key} className={style.item}>*/}
        {/*        <Link*/}
        {/*          href={service.path}*/}
        {/*          passHref*/}
        {/*          onClick={() => {*/}
        {/*            trackEstablishmentPhotoClickEvent({*/}
        {/*              target: "establishment_account",*/}
        {/*            });*/}
        {/*          }}*/}
        {/*        >*/}
        {/*          <div*/}
        {/*            className={style.serviceCard}*/}
        {/*            style={{ minWidth: "120px" }}*/}
        {/*          >*/}
        {/*            {loggedInUser?.company_logo ? (*/}
        {/*              <img*/}
        {/*                className={style.companyLogo}*/}
        {/*                src={buildFileSrc(*/}
        {/*                  `users/${loggedInUser.company_logo}`,*/}
        {/*                  config.IMAGES_CDN_BASE_URL,*/}
        {/*                )}*/}
        {/*                alt="company logo"*/}
        {/*              />*/}
        {/*            ) : (*/}
        {/*              <svg*/}
        {/*                width="30"*/}
        {/*                height="30"*/}
        {/*                viewBox="0 0 30 30"*/}
        {/*                fill="none"*/}
        {/*                xmlns="http://www.w3.org/2000/svg"*/}
        {/*              >*/}
        {/*                <rect*/}
        {/*                  x="0.5"*/}
        {/*                  y="0.5"*/}
        {/*                  width="29"*/}
        {/*                  height="29"*/}
        {/*                  rx="5.5"*/}
        {/*                  fill="#F9FAFB"*/}
        {/*                  stroke="#E1E6EB"*/}
        {/*                />*/}
        {/*                <path*/}
        {/*                  d="M7.78125 10.3271H11.9062V10.8464C11.9062 11.7052 12.6003 12.4041 13.4531 12.4041H16.5469C17.3997 12.4041 18.0937 11.7052 18.0937 10.8464V10.3271H22.2187C22.7973 10.3271 23.25 10.7831 23.25 11.3656V20.7118C23.25 21.2943 22.7973 21.7502 22.2187 21.7502H7.78125C7.20271 21.7502 6.75 21.2943 6.75 20.7118V11.3656C6.75 10.7934 7.21302 10.3271 7.78125 10.3271ZM11.3906 13.4425C10.5378 13.4425 9.84375 14.1414 9.84375 15.0002C9.84375 15.8591 10.5378 16.5579 11.3906 16.5579C12.2435 16.5579 12.9375 15.8591 12.9375 15.0002C12.9375 14.1414 12.2435 13.4425 11.3906 13.4425ZM15.5156 15.5195C15.231 15.5195 15 15.7521 15 16.0387C15 16.3253 15.231 16.5579 15.5156 16.5579H19.6406C19.9253 16.5579 20.1562 16.3253 20.1562 16.0387C20.1562 15.7521 19.9253 15.5195 19.6406 15.5195H15.5156ZM11.3906 17.0742C10.5988 17.0742 9.80632 17.271 9.08497 17.6583C8.91693 17.7487 8.8125 17.9235 8.8125 18.1156V19.1541C8.8125 19.4407 9.0435 19.6733 9.32813 19.6733H13.4531C13.7377 19.6733 13.9687 19.4407 13.9687 19.1541V18.1156C13.9687 17.9235 13.8604 17.7487 13.6934 17.6583C12.9715 17.271 12.1825 17.0742 11.3906 17.0742ZM15.5156 17.5964C15.231 17.5964 15 17.829 15 18.1156C15 18.4022 15.231 18.6348 15.5156 18.6348H19.6406C19.9253 18.6348 20.1562 18.4022 20.1562 18.1156C20.1562 17.829 19.9253 17.5964 19.6406 17.5964H15.5156Z"*/}
        {/*                  fill="#999999"*/}
        {/*                />*/}
        {/*                <path*/}
        {/*                  d="M15 8.25C16.1375 8.25 17.0625 9.18152 17.0625 10.3269V10.8462C17.0625 11.1328 16.8315 11.3654 16.5469 11.3654H13.4531C13.1685 11.3654 12.9375 11.1328 12.9375 10.8462V10.3269C12.9375 9.1815 13.8625 8.25 15 8.25Z"*/}
        {/*                  fill="#999999"*/}
        {/*                />*/}
        {/*              </svg>*/}
        {/*            )}*/}

        {/*            <div className={style.label}>*/}
        {/*              {loggedInUser.company_name}*/}
        {/*            </div>*/}
        {/*          </div>*/}
        {/*        </Link>*/}
        {/*      </div>*/}
        {/*    ) : (*/}
        {/*      <div key={service.name} className={style.item}>*/}
        {/*        <ConditionalWrapper*/}
        {/*          condition={service.disableClientSideNav}*/}
        {/*          wrapper={(children) => <a href={service.path}> {children}</a>}*/}
        {/*          elseWrapper={(children) => {*/}
        {/*            return (*/}
        {/*              <Link href={service.path} passHref>*/}
        {/*                {children}*/}
        {/*              </Link>*/}
        {/*            );*/}
        {/*          }}*/}
        {/*        >*/}
        {/*          <ServiceCard*/}
        {/*            label={service.name}*/}
        {/*            iconProps={{ type: service.icon }}*/}
        {/*            iconClassName={style.serviceIcon}*/}
        {/*          />*/}
        {/*        </ConditionalWrapper>*/}
        {/*      </div>*/}
        {/*    ),*/}
        {/*  )}*/}
        {/*</div>*/}
        {/*<hr />*/}
        <RadioGroup
          defaultValue={currentListings}
          key={currentListings + loggedInUser.listings}
        >
          {USER_LISTINGS_TYPES.map((type) => (
            <RadioGroup.Button
              key={type.value}
              value={type.value}
              className={style.radioBtnWrapper}
              checkedClassName={style.activeRadioBtn}
              onClick={() => handleSwitcherChange(type.value)}
            >
              {type.label}{" "}
              {type.value === "listings"
                ? `(${loggedInUser.listings || 0})`
                : ""}
            </RadioGroup.Button>
          ))}
        </RadioGroup>
      </div>
    );
  }, [loggedInUser, isOwner]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY !== 0) {
        el.scrollLeft += -e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ownerServices]);

  if (error) return null;

  return (
    <div className={style.userPageContainer}>
      <RichOfficeMeta user={user} />
      {isOwner && <AuthProfileCard loading={loading} user={user || {}} />}
      {!isOwner && (
        <ProfileCard
          loading={loading}
          user={user}
          isBroker={!!user?.listings?.length}
        />
      )}
      {ownerServices || (
        <div>
          <hr />
          <h3>إعلانات المستخدم</h3>
        </div>
      )}
      <Component
        expired={currentListings === "expired_listings"}
        drafts={currentListings === "drafts"}
        isOwner={isOwner}
        userId={userId}
        userListings={userListings}
        userListingsTotal={userListingsTotal}
      />
    </div>
  );
}

UserPage.propTypes = {
  initUser: PropTypes.shape({
    user_id: PropTypes.number,
    name: PropTypes.string,
    listings: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  userListings: PropTypes.arrayOf(PropTypes.any),
  userListingsTotal: PropTypes.number,
};

UserPage.defaultProps = {
  initUser: undefined,
  userListings: [],
  userListingsTotal: 0,
};

UserPage.getInitialProps = InitialResolver(async (ctx) => {
  const params = ctx?.query;
  const userId = params?.userId;
  if (hasHinduNumerals(params?.userId)) {
    return ctx.redirect(`/user/${convertToArabicNumerals(params.userId)}`);
  }

  const apolloClient = initApolloClient();
  const { data } = await apolloClient.query({
    skip: !userId,
    query: GET_USER_WITH_REVIEWS,
    variables: { user_id: userId },
  });
  const initUser = {
    ...(data?.User?.get || {}),
    reviewsCount: data?.User?.reviews?.total,
  };
  if (`${ctx.authState?.user?.user_id}` === `${userId}`) {
    const { data: listingsRes } = await apolloClient.query({
      query: GET_OWNER_LISTINGS,
      context: { token: ctx.authState?.token },
      variables: {
        draft: false,
      },
    });

    return dehydrateApolloClient(apolloClient, {
      initUser,
      userId,
      userListings: listingsRes?.Listing?.getMyListings?.listings || [],
      userListingsTotal: listingsRes?.Listing?.getMyListings?.total || 0,
      authState: ctx.authState,
    });
  }

  const maintainedParam = getMaintainedParam(initUser?.cooperative);

  const { data: listingsRes } = await apolloClient.query({
    query: FIND_LISTING,
    variables: {
      size: config.LISTINGS_PAGE_SIZE,
      from: 0,
      sort: config.LISTINGS_DEFAULT_SORTER,
      where: { user_id: { eq: userId }, ...maintainedParam },
    },
  });
  return dehydrateApolloClient(apolloClient, {
    initUser,
    userId,
    userListings: listingsRes?.Web?.find?.listings || [],
    userListingsTotal: listingsRes?.Web?.find?.total || 0,
    authState: ctx.authState,
  });
});
export default UserPage;
